import { Chip } from "../../chips";
import { OptionLink } from "../../links";
import type { PageProps } from "../index";
import { Footnote, PageHead } from "../kit";
import { RowsPage, type RowsData } from "../rows";

export interface GeoGroupRow {
  level: "region" | "country" | "untagged";
  geo_value: string;
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  funnel: string;
  phase: string | null;
  options_total: number;
}

/** Option chips shown per card before "and N more". */
export const CARD_CHIPS = 14;

export interface GeoGroup {
  key: string;
  name: string;
  untagged: boolean;
  count: number;
  /** The first options of the group, at most `CARD_CHIPS`. */
  options: GeoGroupRow[];
}

/** C1 sends tagged rows first and untagged options last, so one untagged row means every tag is already held. */
export const hasEveryTag = (rows: GeoGroupRow[]) => rows.some((row) => row.level === "untagged");

/**
 * One card per tag, biggest first, Untagged last. C1 returns one row per
 * (option, tag) plus one per untagged option, so the untagged count is the
 * row total less the tagged rows. `incomplete` is true when the row cap was
 * hit before the tagged rows ended; the Untagged card is then left out.
 */
export function geoGroups(rows: GeoGroupRow[], total: number): { groups: GeoGroup[]; incomplete: boolean } {
  const tagged = rows.filter((row) => row.level !== "untagged");
  const complete = hasEveryTag(rows) || rows.length >= total;
  const levelsByName = new Map<string, Set<string>>();
  for (const row of tagged) {
    if (!levelsByName.has(row.geo_value)) levelsByName.set(row.geo_value, new Set());
    levelsByName.get(row.geo_value)!.add(row.level);
  }
  const byKey = new Map<string, GeoGroup>();
  for (const row of tagged) {
    const key = `${row.level}:${row.geo_value}`;
    let group = byKey.get(key);
    if (!group) {
      // A tag used at both levels (UK) says which one this card is.
      const both = (levelsByName.get(row.geo_value)?.size ?? 0) > 1;
      group = { key, name: both ? `${row.geo_value} (${row.level})` : row.geo_value, untagged: false, count: 0, options: [] };
      byKey.set(key, group);
    }
    group.count += 1;
    if (group.options.length < CARD_CHIPS) group.options.push(row);
  }
  const groups = Array.from(byKey.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const untaggedCount = total - tagged.length;
  if (complete && untaggedCount > 0) {
    groups.push({
      key: "untagged",
      name: "Untagged",
      untagged: true,
      count: untaggedCount,
      options: rows.filter((row) => row.level === "untagged").slice(0, CARD_CHIPS),
    });
  }
  return { groups, incomplete: !complete };
}

export function GeoGroupsBody({ data }: { data: RowsData<GeoGroupRow> }) {
  const { groups, incomplete } = geoGroups(data.rows, data.total);
  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
        {groups.map((group) => (
          <section key={group.key} className="rounded-[8px] border border-ks-grey-3 p-3">
            <h3 className="mb-2 flex items-center justify-between text-sm font-bold">
              <span>{group.name}</span>
              <Chip tone={group.untagged ? "warn" : "plain"}>{group.count}</Chip>
            </h3>
            <div className="flex flex-wrap gap-2">
              {group.options.map((option) => (
                <Chip key={`${group.key}:${option.option_id}`}>
                  <OptionLink optionId={option.option_id} itemId={option.item_id} from="geo_segmentation_groups">
                    {option.option_name}
                  </OptionLink>
                </Chip>
              ))}
              {group.count > group.options.length ? <Chip tone="muted">and {group.count - group.options.length} more</Chip> : null}
            </div>
          </section>
        ))}
      </div>
      {incomplete ? <Footnote>Too many tags to list in full. Use the filters to narrow the list.</Footnote> : null}
    </>
  );
}

export function GeoGroupsPage({ tab, areaName }: PageProps) {
  return (
    <RowsPage<GeoGroupRow>
      tab={tab}
      areaName={areaName}
      enough={hasEveryTag}
      head={(data) => <PageHead title={tab.label} sub={data ? "Options grouped by country or region" : undefined} />}
    >
      {(data) => <GeoGroupsBody data={data} />}
    </RowsPage>
  );
}
